import { useState } from 'react';
import type { Ctx } from '../../ctx';
import { fmt } from '../../model';
import type { Pipeline, RegisteredWebhook, WebhookEntry } from '../../types';
import ConfirmDelete from '../ConfirmDelete';
import SecretOnce from '../SecretOnce';
import TypedConfirm from '../TypedConfirm';

// Register / rotate create the registration on the source and hand the one-time secretKey to the operator (SecretOnce),
// who stores it in GCP by hand. Delete: external registrations, or one of ours only while another of ours serves the
// same pipeline (the api enforces both).

export default function SettingsTab({
  ctx,
  entry,
  pipeline,
}: {
  ctx: Ctx;
  entry: WebhookEntry;
  pipeline: Pipeline;
}) {
  const { env, entries, data } = ctx;
  const prod = env === 'prod';
  const [del, setDel] = useState<WebhookEntry | null>(null);
  const [reg, setReg] = useState(false);
  const [rot, setRot] = useState<WebhookEntry | null>(null);
  const [made, setMade] = useState<RegisteredWebhook | null>(null);
  const listed = !!data.webhooks?.siteListed;
  const webflow = pipeline.source === 'Webflow';
  const oursFor = (x: WebhookEntry): WebhookEntry[] =>
    entries.filter((e) => e.id !== x.id && e.owner === 'ours' && e.registered === true && e.pipeline === x.pipeline);
  // This pipeline's own registrations plus everything else on the same source.
  const list = entries.filter((e) => e.source === pipeline.source);
  const mine = list.filter((e) => e.pipeline === pipeline.key && e.registered === true);
  const secret = data.secrets.find((s) =>
    pipeline.key === 'tts' ? s.key === 'RW_WEBFLOW_SIGNING_SECRET' : s.key === 'RW_CALENDLY_SIGNING_KEY',
  );

  return (
    <>
      <section className="card">
        <div className="row" style={{ padding: '12px 16px', justifyContent: 'space-between' }}>
          <span>
            <strong>{pipeline.source} registration</strong>{' '}
            <span className="note">
              every webhook on this source, as the service reads it back
              {data.webhooks && !data.webhooks.siteListed ? ' (source listing unavailable)' : ''}
            </span>
          </span>
          <span className="row">
            <button type="button" className="btn sm" onClick={() => data.reload()}>
              Refresh
            </button>
            <button
              type="button"
              className="btn sm primary"
              disabled={!ctx.admin || !webflow || !listed || mine.length > 0}
              title={
                !ctx.admin ? 'admin only'
                  : !webflow ? 'Calendly subscriptions are registered on the Calendly side'
                    : !listed ? 'the source could not be listed'
                      : mine.length ? 'already registered — rotate instead' : `register ${pipeline.route} on ${pipeline.source}`
              }
              onClick={() => setReg(true)}
            >
              + Register for {env}
            </button>
          </span>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th />
                <th>Webhook id</th>
                <th>Trigger</th>
                <th>Target URL</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((x) => (
                <tr key={x.id} style={x.owner !== 'ours' ? { opacity: 0.75 } : undefined}>
                  <td>
                    {x.owner === 'ours' ? (
                      <span className="chip ok">{x.id === entry.id ? 'this webhook' : 'ours'}</span>
                    ) : x.owner === 'zapier' ? (
                      <span className="chip warn">Zapier</span>
                    ) : (
                      <span className="chip info">other</span>
                    )}
                  </td>
                  <td>
                    <code>{x.registered === true ? x.id : '—'}</code>
                  </td>
                  <td>
                    <code>{x.trigger}</code>
                  </td>
                  <td className="wrap">
                    <code>{x.target}</code>
                    {x.note ? <div className="note">{x.note}</div> : null}
                  </td>
                  <td className="dim">{fmt(x.createdOn)}</td>
                  <td>
                    <span className="row" style={{ gap: 6 }}>
                      {x.owner === 'ours' ? (
                        <button
                          type="button"
                          className="btn sm"
                          disabled={!ctx.admin || x.registered !== true || !listed}
                          title={!ctx.admin ? 'admin only' : 'register a second hook for this route; the old one is deleted once the new secret is live'}
                          onClick={() => setRot(x)}
                        >
                          Rotate secret
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn sm danger"
                        disabled={x.registered !== true || !ctx.admin || !listed || (x.owner === 'ours' && !oursFor(x).length)}
                        title={
                          !ctx.admin
                            ? 'admin only'
                            : x.registered !== true || !listed
                              ? 'the source could not be listed'
                              : x.owner === 'ours' && !oursFor(x).length
                                ? 'the only registration feeding this pipeline — rotate first, delete the old one after the new secret is live'
                                : x.owner === 'ours'
                                  ? 'delete this (superseded) registration of ours'
                                  : 'delete this external registration (typed confirmation)'
                        }
                        onClick={() => setDel(x)}
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
              {!list.length ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">Nothing registered on {pipeline.source}.</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="note" style={{ padding: '10px 16px', margin: 0 }}>
          {mine.length ? (
            <>
              Registered ✓ — id <code>{mine[0].id}</code>.{' '}
            </>
          ) : (
            <>
              <strong>Not registered</strong> on this env.{' '}
            </>
          )}
          {pipeline.source === 'Webflow' ? (
            <>
              Webflow sends every form submission to every hook with no filter; this service accepts
              forms matching <code>RW_WEBFLOW_FORM_NAMES</code>.
            </>
          ) : (
            <>Calendly subscriptions are per-organization; this service holds its own or none.</>
          )}
        </p>
      </section>

      <section className="grid g2">
        <div className="card pad">
          <h2>Signing secret</h2>
          <dl className="kv">
            <dt>Key</dt>
            <dd>
              <code>{secret?.key ?? (pipeline.key === 'tts' ? 'RW_WEBFLOW_SIGNING_SECRET' : 'RW_CALENDLY_SIGNING_KEY')}</code>{' '}
              {secret ? (
                secret.set ? (
                  <span className="chip ok">set</span>
                ) : (
                  <span className="chip warn">EMPTY</span>
                )
              ) : (
                <span className="chip">unknown</span>
              )}
            </dd>
            <dt>Live fingerprint</dt>
            <dd>
              {secret?.fingerprint ? (
                <>
                  {secret.fingerprint.split(',').map((f) => (
                    <code key={f} style={{ marginRight: 6 }}>{f}</code>
                  ))}
                  {secret.fingerprint.includes(',') ? <span className="chip info">rotation in flight</span> : null}
                </>
              ) : (
                <span className="faint">—</span>
              )}{' '}
              <span className="note">sha256 prefix of what THIS pod holds; a fresh register/rotate shows the one to wait for</span>
            </dd>
            <dt>Auth</dt>
            <dd className="wrap">{pipeline.auth}</dd>
            <dt>Source</dt>
            <dd>
              GCP row <code>revops--os-revops-webhooks--keys</code> ({prod ? 'prod-secrets' : 'sbx-secrets'}) → k8s
              Secret <code>os-revops-webhooks-env</code>
            </dd>
            <dt>Rotate</dt>
            <dd>
              Rotate secret (above) → store <code>new,current</code> → deploy → delete the old registration → trim to{' '}
              <code>new</code>
            </dd>
          </dl>
          <p className="note" style={{ margin: '8px 0 0' }}>
            The secret is the per-webhook <strong>secretKey</strong> the source returns once at
            create — never the site token.
          </p>
        </div>

        <div className="card pad">
          <h2>Route config</h2>
          <dl className="kv">
            <dt>Route</dt>
            <dd>
              <code>POST {pipeline.route}</code>
            </dd>
            <dt>401 bucket</dt>
            <dd>
              <code>{pipeline.route401}</code>
            </dd>
            <dt>Signature max age</dt>
            <dd>300 s</dd>
            <dt>Env</dt>
            <dd>
              <code>{env}</code> · image <code>{data.service?.imageTag ?? '—'}</code>
            </dd>
            <dt>Branches</dt>
            <dd>{Object.keys(pipeline.steps ?? {}).join(' · ') || '—'}</dd>
          </dl>
          <p className="note" style={{ margin: '8px 0 0' }}>
            Literals live in <code>tools/emit-k8s-secrets.sh</code>; changed by MR.
          </p>
        </div>
      </section>
      {del ? <ConfirmDelete ctx={ctx} entry={del} onClose={() => setDel(null)} /> : null}
      {reg ? (
        <TypedConfirm
          title={`Register ${pipeline.name} on ${pipeline.source} (${env})?`}
          word="REGISTER"
          confirmLabel="Register"
          onClose={() => setReg(false)}
          run={async () => {
            const r = await data.registerWebhook(pipeline.key);
            setReg(false);
            setMade(r);
          }}
        >
          <p className="note" style={{ margin: '0 0 8px' }}>
            Creates a <code>form_submission</code> webhook to <code>{data.webhooks?.baseUrl ?? ''}{pipeline.route}</code>. The
            signing secret comes back once — you store it in GCP by hand.
          </p>
        </TypedConfirm>
      ) : null}
      {rot ? (
        <TypedConfirm
          title="Rotate the signing secret?"
          word={rot.id}
          wordLabel={<>Type the webhook id <code>{rot.id}</code> to confirm</>}
          confirmLabel="Register new hook"
          onClose={() => setRot(null)}
          run={async () => {
            const r = await data.rotateWebhook(rot.id);
            setRot(null);
            setMade(r);
          }}
        >
          <p className="note" style={{ margin: '0 0 8px' }}>
            Registers a <strong>second</strong> hook for the same route. Nothing is deleted now: both deliver until you have
            the new secret live (<code>new,current</code>) and delete <code>{rot.id}</code> yourself.
          </p>
        </TypedConfirm>
      ) : null}
      {made ? <SecretOnce ctx={ctx} made={made} onClose={() => setMade(null)} /> : null}
    </>
  );
}
